import { GlobalSearchResult } from '@/api/global-search';
import { GlobalSearchResultsList } from '@/components/global-search-results-list';
import { AutoAwesomeOutlined } from '@/constants/hardfin-standard-icons';
import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import { MouseEvent } from 'react';

const ResultsContainer = styled(Paper)(() => ({
  width: 400,
  maxHeight: 600,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  padding: 0,
}));

const ScrollableResults = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  paddingY: theme.spacing(1),
}));

/**
 * Props for the SearchResults component
 */
interface SearchResultsProps {
  /** Whether results are currently loading */
  isLoading: boolean;
  /** The current search query */
  searchQuery: string;
  /** Array of entity results to display */
  entityResults: GlobalSearchResult;
  /** Maximum number of results to show per category */
  maxResults: number;
  /** Called when a result is clicked */
  onResultClick: (url: string, event: MouseEvent) => void;
  /** Index of the currently selected result */
  selectedIndex?: number;
  /** Additional styles for the container */
  sx?: React.CSSProperties;
}

/**
 * Displays search results with loading states and empty states.
 * Used by both mobile and desktop search interfaces.
 */
export function GlobalSearchResults({
  isLoading,
  searchQuery,
  entityResults,
  maxResults,
  onResultClick,
  selectedIndex,
  sx = {},
}: SearchResultsProps) {
  const hasResults = Object.values(entityResults).some(
    (results) => results.length > 0
  );

  return (
    <ResultsContainer sx={sx}>
      {isLoading ? (
        <Box display="flex" justifyContent="center" padding={2}>
          <CircularProgress size={20} />
        </Box>
      ) : searchQuery.trim().length > 0 ? (
        !hasResults && searchQuery.length >= 2 ? (
          <Box padding={2} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No results found
            </Typography>
          </Box>
        ) : (
          <>
            <ScrollableResults>
              <GlobalSearchResultsList
                entityResults={entityResults}
                handleResultClick={onResultClick}
                maxResults={maxResults}
                selectedIndex={selectedIndex}
              />
            </ScrollableResults>
            {hasResults && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  padding: (theme) => theme.spacing(1, 2),
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  flexShrink: 0,
                }}
              >
                <AutoAwesomeOutlined
                  fontSize="small"
                  color="primary"
                  sx={{
                    fontSize: 16,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 'medium',
                    color: 'text.secondary',
                  }}
                >
                  Powered by Hardfin AI
                </Typography>
              </Stack>
            )}
          </>
        )
      ) : null}
    </ResultsContainer>
  );
}
